import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './OldBloodTestTable.scss';
import OldBloodTestTableRow from './OldBloodTestTableRow';
import { getMemberBloodTests, BloodTestData } from '../../../api/oldBloodTest';
import { useLocation } from 'react-router-dom';
import BiomarkerExtractionModal from '../BiomarkerExtractionModal';
import { parseBloodReportByFilename, ParsedBiomarker } from '../../../api/corevitals';
import Loader from '../../BaseComponents/Loader';

interface OldBloodTestTableProps {
  onCountUpdate: (count: number) => void;
}

const OldBloodTestTable: React.FC<OldBloodTestTableProps> = ({ onCountUpdate }) => {
  const [bloodTestData, setBloodTestData] = useState<BloodTestData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [, setTotalCount] = useState<number>(0);
  const [, setOffset] = useState<number>(0);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<number>>(new Set());
  const [isBulkExtracting, setIsBulkExtracting] = useState(false);
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false);
  const [parsedBiomarkers, setParsedBiomarkers] = useState<ParsedBiomarker[]>([]);
  const [extractionSummary, setExtractionSummary] = useState<Record<string, any> | undefined>(undefined);
  const [extractionResponseData, setExtractionResponseData] = useState<Record<string, any> | undefined>(undefined);
  const [missingList, setMissingList] = useState<Array<{ biomarker_name: string; standard_unit: string }>>([]);
  const location = useLocation();
  
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const hasMoreRef = useRef<boolean>(true);
  const isLoadingMoreRef = useRef<boolean>(false);
  const offsetRef = useRef<number>(0);
  const limit = 10;
  
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const fetchBloodTestData = useCallback(async (isInitial: boolean = true) => {
    try {
      if (isInitial) {
        setIsLoading(true);
        offsetRef.current = 0;
        setOffset(0);
      } else {
        setIsLoadingMore(true);
      }

      const memberId = location.pathname.split('/')[2];
      
      if (memberId) {
        const currentOffset = offsetRef.current;
        const response = await getMemberBloodTests(memberId, limit, currentOffset);
        const results = response.results || [];
        const totalCount = response.total_count || 0;
        
        if (isInitial) {
          setBloodTestData(results);
          setTotalCount(totalCount);
          onCountUpdate(totalCount);
          setHasMore(results.length === limit && results.length < totalCount);
        } else {
          setBloodTestData(prev => {
            const newData = [...prev, ...results];
            const hasMoreData = results.length > 0 
              && results.length === limit 
              && newData.length < totalCount;
            setHasMore(hasMoreData);
            return newData;
          });
        }
        
        offsetRef.current = currentOffset + limit;
        setOffset(offsetRef.current);
      } else {
        setBloodTestData([]);
        setTotalCount(0);
        onCountUpdate(0);
        setHasMore(false);
        offsetRef.current = 0;
        setOffset(0);
      }
    } catch (error) {
      setBloodTestData([]);
      setTotalCount(0);
      onCountUpdate(0);
      setHasMore(false);
      offsetRef.current = 0;
      setOffset(0);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [location.pathname, limit, onCountUpdate]);

  const lastBloodTestElementRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
        fetchBloodTestData(false);
      }
    });
    
    if (node) {
      observer.current.observe(node);
    }
  }, [isLoading, fetchBloodTestData]);

  useEffect(() => {
    fetchBloodTestData(true);
  }, [fetchBloodTestData]);

  const memberId = location.pathname.split('/')[2];

  const isImageFile = useCallback((fileName: string) => {
    return /\.(png|jpe?g|heic)$/i.test(fileName || '');
  }, []);

  const selectedImageRows = useMemo(() => {
    if (selectedImageIds.size === 0) return [];
    return bloodTestData.filter(bt => selectedImageIds.has(bt.id) && isImageFile(bt.file_name));
  }, [bloodTestData, selectedImageIds, isImageFile]);

  const handleToggleImageSelected = useCallback((id: number, nextSelected: boolean) => {
    setSelectedImageIds(prev => {
      const next = new Set(prev);
      if (nextSelected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleBulkExtract = useCallback(async () => {
    if (selectedImageRows.length === 0) return;
    try {
      setIsBulkExtracting(true);

      const filename = selectedImageRows.map(r => r.file_name).join(',');
      const filePath = selectedImageRows.map(r => r.file_path).join(',');

      const response = await parseBloodReportByFilename(filename, filePath);

      if (response.success && ((response.data as any)?.matched_biomarkers || response.data?.biomarkers)) {
        const data = response.data as any;
        const biomarkers = data.matched_biomarkers || response.data.biomarkers || [];
        setParsedBiomarkers(biomarkers);
        setExtractionSummary(response.data.summary);
        setExtractionResponseData(response.data);
        setMissingList(data.missing_list || []);
        setIsExtractionModalOpen(true);
      } else {
        alert(response.error || 'Failed to extract biomarkers from the report');
      }
    } catch (error: any) {
      console.error('Bulk extraction error:', error);
      const errorMessage =
        error?.response?.data?.error || error?.message || 'Failed to extract report data. Please try again.';
      alert(errorMessage);
    } finally {
      setIsBulkExtracting(false);
    }
  }, [selectedImageRows]);

  if (isLoading) {
    return (
      <div className='scroll-container'>
        <div className='table-container old-blood-test-table'>
          <div className='bulk-actions'>
            <button className='extract-selected-btn' disabled>
              Extract Selected
            </button>
          </div>
          <div className='table-header'>
            <div className='header-column select-column'></div>
            <div className='header-column'>Date ↓</div>
            <div className='header-column'>Name</div>
            <div className='header-column'>Status</div>
            <div className='header-column'></div>
          </div>
          <div className='table-body'>
            <div className='loading-message'>Loading blood test data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='scroll-container'>
      <div className='table-container old-blood-test-table'>
        <div className='bulk-actions'>
          <button
            className='extract-selected-btn'
            onClick={handleBulkExtract}
            disabled={selectedImageRows.length === 0 || isBulkExtracting}
            title={selectedImageRows.length === 0 ? 'Select image files to extract' : undefined}
          >
            {isBulkExtracting ? 'Extracting...' : `Extract Selected${selectedImageRows.length ? ` (${selectedImageRows.length})` : ''}`}
          </button>
        </div>
        <div className='table-header'>
          <div className='header-column select-column'></div>
          <div className='header-column'>Date ↓</div>
          <div className='header-column'>Name</div>
          <div className='header-column'>Status</div>
          <div className='header-column'></div>
        </div>
        <div className='member-table-body'>
          {bloodTestData.length > 0 ? (
            bloodTestData.map((bloodTest, index) => {
              const isLastElement = index === bloodTestData.length - 1;
              return (
                <div key={bloodTest.id} ref={isLastElement ? lastBloodTestElementRef : undefined}>
                  <OldBloodTestTableRow
                    bloodTestData={bloodTest}
                    onRefresh={fetchBloodTestData}
                    showSelectCheckbox={isImageFile(bloodTest.file_name)}
                    isSelected={selectedImageIds.has(bloodTest.id)}
                    onSelectedChange={handleToggleImageSelected}
                  />
                </div>
              );
            })
          ) : (
            <div className='empty-message'>No blood test data available</div>
          )}
          {isLoadingMore && (
            <div className='loading-message'>Loading more blood test data...</div>
          )}
        </div>

        {isBulkExtracting && (
          <div className='bulk-extraction-overlay'>
            <Loader />
            <span className='bulk-extraction-text'>Extracting selected image reports...</span>
          </div>
        )}
      </div>

      <BiomarkerExtractionModal
        isOpen={isExtractionModalOpen}
        onClose={() => {
          setIsExtractionModalOpen(false);
          setSelectedImageIds(new Set());
        }}
        userId={memberId}
        parsedBiomarkers={parsedBiomarkers}
        summary={extractionSummary}
        responseData={extractionResponseData}
        missingList={missingList}
        onSaveSuccess={() => {
          setIsExtractionModalOpen(false);
          setSelectedImageIds(new Set());
          fetchBloodTestData(true);
        }}
      />
    </div>
  );
};

export default OldBloodTestTable;
